import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Calculator,
  CheckCircle2,
  Loader2,
  Radar,
  Search,
  ShieldAlert,
} from "lucide-react";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import {
  calculateFinancialExposure,
  checkRegistryVat,
  getWorkflowConsistency,
  getWorkflowReadiness,
  screenSanctions,
  setComplianceContext,
} from "../../services/compliance-api";
import type {
  FinancialExposureResponse,
  RegistryCheckResponse,
  SanctionsScreenResponse,
  WorkflowConsistencyResponse,
  WorkflowReadinessResponse,
} from "../../types/compliance";

const shipmentTypes = [
  "eu_import",
  "eu_export",
  "eu_cross_border_road",
  "sea_freight",
  "air_freight",
  "dangerous_goods",
  "food_agri",
  "pharma_medical",
  "controlled_goods",
];

function scoreColor(score: number) {
  if (score >= 85) return "text-emerald-700";
  if (score >= 60) return "text-amber-700";
  return "text-red-700";
}

function decisionBadge(decision?: string) {
  if (!decision) return "bg-gray-100 text-gray-700";
  if (decision === "ready_for_approval" || decision === "valid") {
    return "bg-emerald-100 text-emerald-700";
  }
  if (decision === "blocked" || decision === "block_workflow") {
    return "bg-red-100 text-red-700";
  }
  return "bg-amber-100 text-amber-700";
}

export default function V2IntelligencePanel() {
  const { credentials } = useComplianceAuth();
  const tenantId = credentials?.tenantId ?? "default";
  const role = credentials?.role ?? "COMPLIANCE_ADMIN";
  const [workflowId, setWorkflowId] = useState("");
  const [shipmentType, setShipmentType] = useState("eu_import");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<WorkflowReadinessResponse | null>(null);
  const [consistency, setConsistency] = useState<WorkflowConsistencyResponse | null>(null);
  const [vatCountry, setVatCountry] = useState("DE");
  const [vatNumber, setVatNumber] = useState("");
  const [registry, setRegistry] = useState<RegistryCheckResponse | null>(null);
  const [partyName, setPartyName] = useState("");
  const [sanctionsName, setSanctionsName] = useState("");
  const [sanctions, setSanctions] = useState<SanctionsScreenResponse | null>(null);
  const [declaredValue, setDeclaredValue] = useState("10000");
  const [dutyRate, setDutyRate] = useState("6.5");
  const [delayDays, setDelayDays] = useState("3");
  const [delayCost, setDelayCost] = useState("1200");
  const [exposure, setExposure] = useState<FinancialExposureResponse | null>(null);

  useEffect(() => {
    setComplianceContext(tenantId, role);
  }, [tenantId, role]);

  const discrepancyTypes = useMemo(
    () => consistency?.discrepancies.map((item) => String(item.risk_type)) ?? [],
    [consistency],
  );

  async function runWorkflowIntelligence() {
    if (!workflowId.trim()) {
      setError("Enter a workflow ID first.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [readinessResult, consistencyResult] = await Promise.all([
        getWorkflowReadiness(workflowId.trim(), shipmentType),
        getWorkflowConsistency(workflowId.trim()),
      ]);
      setReadiness(readinessResult);
      setConsistency(consistencyResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : "V2 workflow intelligence failed");
    } finally {
      setLoading(false);
    }
  }

  function normalizeVatInput(country: string, number: string) {
    const compactNumber = number.replace(/[\s.-]/g, "").toUpperCase();
    const fullVatMatch = compactNumber.match(/^([A-Z]{2})([A-Z0-9]{2,})$/);
    if (fullVatMatch) {
      return {
        countryCode: fullVatMatch[1],
        vatNumber: fullVatMatch[2],
      };
    }
    return {
      countryCode: (country.trim() || "DE").toUpperCase(),
      vatNumber: compactNumber,
    };
  }

  function handleVatNumberChange(value: string) {
    setVatNumber(value);
    const compactValue = value.replace(/[\s.-]/g, "").toUpperCase();
    const fullVatMatch = compactValue.match(/^([A-Z]{2})([A-Z0-9]{2,})$/);
    if (fullVatMatch) {
      setVatCountry(fullVatMatch[1]);
    }
  }

  async function runVatCheck() {
    const normalized = normalizeVatInput(vatCountry, vatNumber);
    if (!normalized.vatNumber) return;
    setVatCountry(normalized.countryCode);
    setVatNumber(normalized.vatNumber);
    setRegistry(await checkRegistryVat(normalized.countryCode, normalized.vatNumber));
  }

  async function runSanctionsCheck() {
    if (!partyName.trim() || !sanctionsName.trim()) return;
    setSanctions(
      await screenSanctions({
        workflow_id: workflowId.trim() || null,
        parties: [partyName.trim()],
        sanctions_entries: [{ name: sanctionsName.trim(), source: "manual" }],
      }),
    );
  }

  async function runFinancialExposure() {
    setExposure(
      await calculateFinancialExposure({
        workflow_id: workflowId.trim() || "manual-v2-exposure",
        declared_value: Number(declaredValue) || 0,
        duty_rate_pct: Number(dutyRate) || 0,
        delay_days: Number(delayDays) || 0,
        delay_cost_per_day: Number(delayCost) || 0,
      }),
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Radar className="text-blue-700" size={24} />
            <h1 className="text-2xl font-bold text-gray-900">V2 Intelligence</h1>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Production readiness, evidence consistency, registry safety, sanctions screening, and financial exposure.
          </p>
        </div>
        <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
          AI assistive · rules decide
        </span>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_220px_auto] md:items-end">
          <label className="block">
            <span className="text-xs font-semibold uppercase text-gray-500">Workflow ID</span>
            <input
              value={workflowId}
              onChange={(event) => setWorkflowId(event.target.value)}
              placeholder="wf_..."
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase text-gray-500">Shipment Type</span>
            <select
              value={shipmentType}
              onChange={(event) => setShipmentType(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              {shipmentTypes.map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void runWorkflowIntelligence()}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            Analyze
          </button>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Readiness Scores</h2>
            {readiness && (
              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${decisionBadge(readiness.decision)}`}>
                {readiness.decision.replace(/_/g, " ")}
              </span>
            )}
          </div>
          {readiness ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs uppercase text-gray-500">Shipment</p>
                  <p className={`text-3xl font-bold ${scoreColor(readiness.shipment_readiness_score)}`}>
                    {readiness.shipment_readiness_score}%
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs uppercase text-gray-500">Audit</p>
                  <p className={`text-3xl font-bold ${scoreColor(readiness.audit_readiness_score)}`}>
                    {readiness.audit_readiness_score}%
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-gray-500">Missing Required Evidence</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {readiness.missing_required_documents.length ? (
                    readiness.missing_required_documents.map((doc) => (
                      <span key={doc} className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
                        {doc.replace(/_/g, " ")}
                      </span>
                    ))
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
                      <CheckCircle2 size={15} /> Required evidence present
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Run analysis to load readiness scores.</p>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Consistency Checks</h2>
            {consistency && (
              <span className={`text-sm font-semibold ${scoreColor(Math.round(consistency.overall_score * 100))}`}>
                {Math.round(consistency.overall_score * 100)}%
              </span>
            )}
          </div>
          {consistency ? (
            <div className="space-y-3">
              {discrepancyTypes.length ? (
                discrepancyTypes.map((type) => (
                  <div key={type} className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <AlertTriangle size={16} />
                    {type.replace(/_/g, " ")}
                  </div>
                ))
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  <CheckCircle2 size={16} />
                  No cross-document discrepancies detected.
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Run analysis to compare extracted shipment evidence.</p>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 font-semibold text-gray-900">Registry Safety Check</h2>
          <div className="grid gap-3 sm:grid-cols-[90px_1fr_auto]">
            <input
              value={vatCountry}
              onChange={(event) => setVatCountry(event.target.value.toUpperCase())}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              value={vatNumber}
              onChange={(event) => handleVatNumberChange(event.target.value)}
              placeholder="VAT number or full VAT ID, e.g. IE1234567X"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => void runVatCheck()}
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
            >
              Check VIES
            </button>
          </div>
          {registry && (
            <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm">
              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${decisionBadge(registry.decision)}`}>
                {registry.decision.replace(/_/g, " ")}
              </span>
              <p className="mt-2 text-gray-600">
                Status: {registry.status} · Valid: {registry.valid ? "yes" : "no"}
              </p>
              {registry.details?.country_code && registry.details?.vat_number && (
                <p className="mt-1 font-mono text-xs text-gray-500">
                  Checked: {registry.details.country_code}/{registry.details.vat_number}
                </p>
              )}
              {registry.details?.registered_name && registry.details.registered_name !== "---" && (
                <p className="mt-1 text-gray-600">
                  Registered: {registry.details.registered_name}
                </p>
              )}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
            <ShieldAlert size={18} className="text-red-600" /> Sanctions Screening
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              value={partyName}
              onChange={(event) => setPartyName(event.target.value)}
              placeholder="Party name"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              value={sanctionsName}
              onChange={(event) => setSanctionsName(event.target.value)}
              placeholder="Sanctions entry name"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => void runSanctionsCheck()}
            className="mt-3 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black"
          >
            Screen Party
          </button>
          {sanctions && (
            <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm">
              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${decisionBadge(sanctions.blocked ? "blocked" : "manual_review_required")}`}>
                {sanctions.blocked ? "blocked" : sanctions.manual_review_required ? "manual review" : "clear"}
              </span>
              <p className="mt-2 text-gray-600">Matches: {sanctions.matches.length}</p>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
          <h2 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
            <Calculator size={18} className="text-emerald-600" /> Financial Exposure
          </h2>
          <div className="grid gap-3 md:grid-cols-5">
            {[
              ["Declared value", declaredValue, setDeclaredValue],
              ["Duty %", dutyRate, setDutyRate],
              ["Delay days", delayDays, setDelayDays],
              ["Delay €/day", delayCost, setDelayCost],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block">
                <span className="text-xs font-semibold uppercase text-gray-500">{label as string}</span>
                <input
                  value={value as string}
                  onChange={(event) => (setter as (value: string) => void)(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
            ))}
            <button
              type="button"
              onClick={() => void runFinancialExposure()}
              className="self-end rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
            >
              Calculate
            </button>
          </div>
          {exposure && (
            <div className="mt-4 grid gap-3 sm:grid-cols-4">
              {[
                ["Duty", exposure.duty_exposure],
                ["Delay", exposure.delay_exposure],
                ["Penalty", exposure.penalty_exposure],
                ["Total", exposure.total_exposure],
              ].map(([label, value]) => (
                <div key={label as string} className="rounded-lg bg-gray-50 p-3">
                  <p className="text-xs uppercase text-gray-500">{label as string}</p>
                  <p className="text-xl font-bold text-gray-900">€{Number(value).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

